import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useAuth from '../../../base/hooks/useAuth'
import { useUtils } from '../../../base/hooks/useUtils.tsx'
import { useComandaCambiarEstado } from '../../restaurante/api/apiComandaCambiarEstado.ts'
import {
  apiComandasCocinaNotificacion,
  type ComandaCocinaNotificacionProps,
} from '../../restaurante/api/apiComandaCocinaNotificacion.ts'
import {
  COCINA_VERSION_NO_DISPONIBLE,
  useComandasCocinaVersion,
} from '../../restaurante/api/apiComandaCocinaVersion.ts'
import { ESTADO_COCINA, type EstadoCocinaProps } from '../../restaurante/interfaces/comanda.ts'
import AvisosCocinaPila from '../components/AvisosCocinaPila.tsx'
import { useAreasPantalla } from '../hooks/useAreasPantalla.ts'
import COCINA_RECARGA_SIN_REDIS_MS, {
  COCINA_INTERVALO_MS,
  esConflictoEstadoCocina,
} from '../hooks/useComandasCocinaSync.ts'
import { getDestinoComanda, keyComandaArea, parseFechaComanda, sonarAviso } from '../utils/comandaCocina.ts'
import { type AvisoCocina, AvisosCocinaContext, type AvisosCocinaContextType } from './avisosCocinaContext.ts'

const SONIDO_KEY = 'restaurante:avisos-cocina:sonido'

/** Clave de versión de Redis que cambia con cada cambio de estado de cocina de la sucursal */
const VERSION_ESTADOS = ['ESTADOS']

/**
 * Estados que se piden a restCocinaNotificaciones. Además de LISTO (crea el aviso) se piden los
 * que lo cierran o lo invalidan: ENTREGADO (otro equipo lo entregó), EN_PREPARACION (cocina lo
 * regresó) y ANULADA (cancelación o corrección del pedido).
 */
const ESTADOS_NOTIFICACION: EstadoCocinaProps[] = [
  ESTADO_COCINA.EN_PREPARACION,
  ESTADO_COCINA.LISTO,
  ESTADO_COCINA.ENTREGADO,
  ESTADO_COCINA.ANULADA,
]

const leerSonido = () => {
  try {
    return localStorage.getItem(SONIDO_KEY) !== 'false'
  } catch {
    return true
  }
}

const tiempo = (fecha: string | null | undefined) => parseFechaComanda(fecha)?.getTime() ?? 0

const avisoDesdeNotificacion = (n: ComandaCocinaNotificacionProps): AvisoCocina => ({
  key: keyComandaArea(n.comandaId, n.area),
  comandaId: n.comandaId,
  area: n.area,
  areaNombre: n.areaNombre,
  pedidoId: n.pedidoId,
  numero: n.numeroOrden || n.numeroPedido || 0,
  destino: getDestinoComanda(n),
  fechaListo: n.fecha,
  usuario: n.usuario,
})

/**
 * Aplica las notificaciones, en orden cronológico, sobre los avisos actuales.
 * - LISTO crea o actualiza el aviso, salvo que el área ya haya avanzado (estadoActual distinto).
 * - Cualquier otro estado cierra el aviso de esa comanda y área.
 * Devuelve el nuevo mapa y las claves de los avisos que no existían.
 */
const aplicarNotificaciones = (
  actuales: ReadonlyMap<string, AvisoCocina>,
  notificaciones: ComandaCocinaNotificacionProps[],
) => {
  const siguiente = new Map(actuales)
  const nuevos: string[] = []
  ;[...notificaciones]
    .sort((a, b) => tiempo(a.fecha) - tiempo(b.fecha))
    .forEach((n) => {
      const key = keyComandaArea(n.comandaId, n.area)
      const sigueListo =
        n.estado === ESTADO_COCINA.LISTO &&
        (n.estadoActual === null || n.estadoActual === ESTADO_COCINA.LISTO)
      if (!sigueListo) {
        siguiente.delete(key)
        return
      }
      if (!siguiente.has(key)) nuevos.push(key)
      siguiente.set(key, avisoDesdeNotificacion(n))
    })
  return { siguiente, nuevos }
}

interface AvisosCocinaProviderProps {
  children: ReactNode
  /** Milisegundos entre consultas de versión. Por defecto 3000 */
  intervalo?: number
}

/**
 * Notificador del punto de venta: avisos persistentes y discretos cuando cocina marca LISTO.
 * 1. Cada `intervalo` consulta `restCocinaVersion(codigoSucursal, ["ESTADOS"])` (Redis).
 * 2. Si la versión cambió, pide `restCocinaNotificaciones(codigoSucursal, cursor, codigoPuntoVenta)`
 *    con el cursor de la respuesta anterior: solo llega lo nuevo y se acumula en los avisos.
 * 3. Si Redis no está disponible (versión -1), pide notificaciones cada 15 s.
 * El aviso se cierra al marcarlo ENTREGADO (aquí, en otro equipo o en cocina).
 * No se activa si la sucursal no tiene áreas con pantalla. Se monta como ruta layout en
 * restauranteRoutes: solo consulta en las vistas de pedidos.
 */
export const AvisosCocinaProvider = ({
  children,
  intervalo = COCINA_INTERVALO_MS,
}: AvisosCocinaProviderProps) => {
  const { user } = useAuth()
  const { toast, showError } = useUtils()
  // El código 0 es válido (casa matriz): se valida que exista, no que sea truthy
  const codigoSucursal: number = user?.sucursal?.codigo ?? 0
  const haySucursal = user?.sucursal?.codigo != null
  const codigoPuntoVenta: number | null = user?.puntoVenta?.codigo ?? null

  // Solo se consulta si la sucursal tiene alguna área con pantalla (las demás no tienen estado)
  const { ids: idsPantalla } = useAreasPantalla(codigoSucursal, haySucursal)
  const activo = haySucursal && idsPantalla.length > 0

  // --- Preferencias de este equipo ---
  const [sonido, setSonidoState] = useState(leerSonido)
  const sonidoRef = useRef(sonido)
  const setSonido = useCallback((value: boolean) => {
    setSonidoState(value)
    sonidoRef.current = value
    try {
      localStorage.setItem(SONIDO_KEY, String(value))
    } catch {
      // sin almacenamiento: dura la sesión
    }
  }, [])

  // --- Avisos acumulados (el ref permite calcular sobre el último valor fuera del render) ---
  const [avisosMap, setAvisosMap] = useState<ReadonlyMap<string, AvisoCocina>>(() => new Map())
  const avisosRef = useRef<ReadonlyMap<string, AvisoCocina>>(avisosMap)
  const reemplazarAvisos = useCallback((siguiente: ReadonlyMap<string, AvisoCocina>) => {
    avisosRef.current = siguiente
    setAvisosMap(siguiente)
  }, [])
  const quitarAviso = useCallback(
    (key: string) => {
      if (!avisosRef.current.has(key)) return
      const siguiente = new Map(avisosRef.current)
      siguiente.delete(key)
      reemplazarAvisos(siguiente)
    },
    [reemplazarAvisos],
  )

  const avisos = useMemo(
    () => [...avisosMap.values()].sort((a, b) => tiempo(a.fechaListo) - tiempo(b.fechaListo)),
    [avisosMap],
  )

  // --- Carga de notificaciones con cursor ---
  const cursorRef = useRef<string | null>(null)
  const inicializadoRef = useRef(false)
  const enCursoRef = useRef(false)
  const ultimaCargaRef = useRef(0)

  /** Pide las notificaciones nuevas desde el último cursor. Devuelve true si se completó */
  const cargarNotificaciones = useCallback(async () => {
    if (enCursoRef.current) return false
    enCursoRef.current = true
    try {
      const respuesta = await apiComandasCocinaNotificacion({
        codigoSucursal,
        codigoPuntoVenta,
        cursor: cursorRef.current,
        estados: ESTADOS_NOTIFICACION,
      })
      cursorRef.current = respuesta.cursor ?? cursorRef.current
      ultimaCargaRef.current = Date.now()
      // La primera respuesta trae lo pendiente: se muestra sin sonar
      const inicial = !inicializadoRef.current
      inicializadoRef.current = true

      if (respuesta.notificaciones.length === 0) return true
      const { siguiente, nuevos } = aplicarNotificaciones(avisosRef.current, respuesta.notificaciones)
      reemplazarAvisos(siguiente)
      if (!inicial && nuevos.length > 0 && sonidoRef.current) sonarAviso()
      return true
    } catch (error) {
      // Los avisos son secundarios: un fallo no interrumpe al usuario, queda en consola
      console.error('[AvisosCocina] No se pudieron consultar las notificaciones de cocina', error)
      return false
    } finally {
      enCursoRef.current = false
    }
  }, [codigoSucursal, codigoPuntoVenta, reemplazarAvisos])

  // --- Versión de estados (Redis) ---
  const versionQuery = useComandasCocinaVersion(
    { codigoSucursal, areas: VERSION_ESTADOS },
    {
      enabled: activo,
      refetchInterval: intervalo,
      refetchIntervalInBackground: true,
      staleTime: 0,
      retry: 1,
    },
  )
  const { data: versiones, dataUpdatedAt, error: versionError } = versionQuery

  useEffect(() => {
    if (versionError) console.error('[AvisosCocina] No se pudo consultar la versión de estados', versionError)
  }, [versionError])

  // Cada respuesta de versión se evalúa una sola vez
  const versionCargadaRef = useRef<number | null>(null)
  const atendidaRef = useRef(0)
  useEffect(() => {
    if (!versiones || !dataUpdatedAt || atendidaRef.current === dataUpdatedAt) return
    atendidaRef.current = dataUpdatedAt
    const version = versiones[0]?.version ?? COCINA_VERSION_NO_DISPONIBLE

    if (version === COCINA_VERSION_NO_DISPONIBLE) {
      // Redis caído: se piden notificaciones cada 15 s
      if (Date.now() - ultimaCargaRef.current >= COCINA_RECARGA_SIN_REDIS_MS) void cargarNotificaciones()
      return
    }
    // La primera respuesta también carga (cursor null): trae lo pendiente al entrar
    if (version === versionCargadaRef.current) return
    void cargarNotificaciones().then((ok) => {
      // Si falló, la siguiente consulta de versión lo reintenta
      if (ok) versionCargadaRef.current = version
    })
  }, [versiones, dataUpdatedAt, cargarNotificaciones])

  // --- Minimizar: se recuerdan las claves ocultas; un aviso nuevo no está ahí y se muestra ---
  const [ocultas, setOcultas] = useState<ReadonlySet<string>>(() => new Set())
  const visibles = useMemo(() => avisos.filter((a) => !ocultas.has(a.key)), [avisos, ocultas])
  const minimizar = useCallback(() => setOcultas(new Set(avisos.map((a) => a.key))), [avisos])
  const mostrar = useCallback(() => setOcultas(new Set()), [])

  // --- Entrega ---
  const { mutateAsync: cambiarEstado } = useComandaCambiarEstado()
  const [procesando, setProcesando] = useState<ReadonlySet<string>>(() => new Set())

  const entregarUno = useCallback(
    async (aviso: AvisoCocina, mostrarError: boolean) => {
      setProcesando((p) => new Set(p).add(aviso.key))
      try {
        await cambiarEstado({
          comandaId: aviso.comandaId,
          area: aviso.area,
          estado: ESTADO_COCINA.ENTREGADO,
          estadoEsperado: ESTADO_COCINA.LISTO,
        })
        quitarAviso(aviso.key)
      } catch (error) {
        if (esConflictoEstadoCocina(error)) {
          // Ya no está LISTO (otro equipo lo entregó o cocina lo regresó): se quita y se sincroniza
          quitarAviso(aviso.key)
          void cargarNotificaciones()
        } else if (mostrarError) {
          showError(error)
        } else {
          throw error
        }
      } finally {
        setProcesando((p) => {
          const siguiente = new Set(p)
          siguiente.delete(aviso.key)
          return siguiente
        })
      }
    },
    [cambiarEstado, quitarAviso, cargarNotificaciones, showError],
  )

  const entregar = useCallback((aviso: AvisoCocina) => entregarUno(aviso, true), [entregarUno])

  const entregarTodos = useCallback(async () => {
    let fallidos = 0
    for (const aviso of avisos) {
      try {
        await entregarUno(aviso, false)
      } catch {
        fallidos++
      }
    }
    if (fallidos) toast.warning(`No se pudieron entregar ${fallidos} aviso(s). Intente nuevamente.`)
  }, [avisos, entregarUno, toast])

  const value = useMemo<AvisosCocinaContextType>(
    () => ({ avisos, visibles, procesando, entregar, entregarTodos, minimizar, mostrar, sonido, setSonido }),
    [avisos, visibles, procesando, entregar, entregarTodos, minimizar, mostrar, sonido, setSonido],
  )

  return (
    <AvisosCocinaContext.Provider value={value}>
      {children}
      {activo && <AvisosCocinaPila />}
    </AvisosCocinaContext.Provider>
  )
}
